import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, ShieldCheck, FileText, Wallet, Eye, EyeOff, Loader2 } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwZGF5bGlnaHR8ZW58MHx8fHwxNzgwMjU3OTA4fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Por favor, completa todos los campos.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (msg) {
        setError(msg);
      } else if (err?.message?.includes("ERR_CONNECTION_REFUSED") || err?.message?.includes("Network Error")) {
        setError("No se puede conectar con el servidor (backend). Asegúrate de que está arrancado en http://localhost:8000.");
      } else {
        setError("Ha ocurrido un error. Inténtalo de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 lg:p-14">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 bg-primary rounded-md flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" strokeWidth={2} />
          </div>
          <span className="font-display font-semibold text-lg tracking-tight">RentaPiso</span>
        </div>

        {/* Form */}
        <div className="max-w-md w-full">
          <p className="overline mb-3">Gestión de alquileres</p>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-foreground leading-tight mb-2">
            Bienvenido de nuevo
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            Accede a tus pisos, inquilinos y pagos. Las cuentas se crean desde Administración.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="password">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-md font-medium text-sm transition-all duration-200 hover:bg-foreground/90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Iniciar sesión
            </button>
          </form>

          {/* Feature highlights */}
          <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-border">
            {[
              { icon: Wallet, label: "Cobros" },
              { icon: FileText, label: "Contratos" },
              { icon: ShieldCheck, label: "IRPF" },
            ].map((f) => (
              <div key={f.label} className="flex flex-col items-start gap-2">
                <f.icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                <span className="text-xs text-muted-foreground font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} RentaPiso · Gestión privada
        </p>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={LOGIN_BG} alt="Edificio" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent mix-blend-multiply" />
      </div>
    </div>
  );
}
